<template>
  <scoped>
    <VerticalNavigator>
      <Breadcrumb
        v-if="supplier"
        :links="[{
          title: 'RutSolve',
          routeName: 'index'
        }, {
          title: 'Suppliers',
          routeName: 'suppliers'
        }]"
        :title="supplier.name"
      />
      <h1>
        Edit Supplier
      </h1>
      <SupplierEditor v-model:value="supplier" />
      <h2>
        Contact Details
      </h2>
      <ContactDetailsList
        v-if="supplier && supplier.contactDetails"
        v-model:items="supplier.contactDetails.records"
        v-model:selectedIndex="selectedContactDetailsIndex"
        @select="selectedContactDetails = supplier.contactDetails.records[selectedContactDetailsIndex]"
      />
      <ContentModal v-if="selectedContactDetails">
        <ContactDetailsEditor v-model:value="selectedContactDetails" />
        <br>
        <Button
          title="Apply"
          @click="selectedContactDetails = null"
        />
      </ContentModal>
      <Button
        title="Remove"
        @click="confirmRemove = true"
      />
      <Button
        title="Cancel"
        @click="suppliersRedirect()"
      />
      <Button
        title="Save"
        @click="save()"
      />
    </VerticalNavigator>
    <ConfirmationModal
      v-model:display="confirmRemove"
      :callback="remove"
      message="This action will remove selected supplier."
      title="Remove Supplier"
    />
    <ErrorModal
      v-if="error"
      v-model:error="error"
    />
  </scoped>
</template>

<script setup lang="ts">
  import Button from "@/components/button.vue"
  import SupplierEditor from "@/components/editors/supplier.vue"
  import VerticalNavigator from "@/layouts/vertical_navigator.vue"
  import Breadcrumb from "@/components/breadcrumb.vue"
  import {useRoute} from "vue-router"
  import {Supplier as SupplierClient} from "@/endpoints/rutsolve/gql/resolvers/supplier"
  import {ContactDetails as ContactDetailsClient} from "@/endpoints/rutsolve/gql/resolvers/contact_details"
  import {onBeforeMount, ref} from "vue"
  import {ContactDetails, Supplier} from "@/endpoints/rutsolve/gql/generated/graphql"
  import ErrorModal from "@/components/modals/error.vue"
  import ConfirmationModal from "@/components/modals/confirmation.vue"
  import router from "@/router"
  import ContactDetailsList from "@/components/lists/contact_details.vue"
  import ContactDetailsEditor from "@/components/editors/contact_details.vue"
  import ContentModal from "@/components/modals/content.vue"

  const error = ref<Error>(null)
  const contactDetailsClient = new ContactDetailsClient()
  const supplierClient = new SupplierClient()
  const supplier = ref<Supplier>(null)
  const confirmRemove = ref<boolean>(false)
  const selectedContactDetails = ref<ContactDetails>(null)
  const selectedContactDetailsIndex = ref<number>(null)

  onBeforeMount(() => {
    read()
  })

  async function read() {
    try {
      const supplierID = useRoute().params.supplierID.toString()
      supplier.value = await supplierClient.summary(supplierID)
      if (!supplier.value.contactDetails || !supplier.value.contactDetails.records) {
        supplier.value.contactDetails = {
          records: []
        }
      }
    } catch(e) {
      error.value = e
    }
  }

  async function remove() {
    try {
      await supplierClient.remove(supplier.value.id)
      suppliersRedirect()
    } catch(e) {
      error.value = e
    }
  }

  async function save() {
    try {
      await supplierClient.update(supplier.value)
      for (const contactDetails of supplier.value.contactDetails.records) {
        contactDetails.relationID = supplier.value.id
        contactDetails.relationType = "SUPPLIER"
        if (contactDetails.deletedAt) {
          await contactDetailsClient.remove(contactDetails.id)
        } else if (contactDetails.id) {
          await contactDetailsClient.update(contactDetails)
        } else {
          await contactDetailsClient.create(contactDetails)
        }
      }
      suppliersRedirect()
    } catch(e) {
      error.value = e
    }
  }
  
  function suppliersRedirect() {
    router.push({
      name: "suppliers"
    })
  }
</script>
